import React from "react";
import { Clock, History, MessageSquare, MessageSquarePlus } from "lucide-react";

import { Button } from "../ui/button";
import { cn } from "../../utils/tailwindUtils";
import { getDomainMeta } from "./constants";

const relativeDate = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";

    const today = new Date();
    const isSameDay = date.toDateString() === today.toDateString();
    if (isSameDay) {
        return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    }

    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    if (date.toDateString() === yesterday.toDateString()) return "Yesterday";

    return date.toLocaleDateString([], { day: "2-digit", month: "short" });
};

/**
 * The caller's saved conversations. Sessions are per-employee on the backend, so
 * this only ever lists the signed-in user's own threads.
 */
const CopilotSessionList = ({
    sessions,
    activeSessionId,
    loading,
    onSelect,
    onNewChat,
}) => (
    <div className="flex h-full flex-col">
        <div className="flex items-center justify-between gap-2 border-b border-border px-3 py-2.5">
            <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                <History className="h-3.5 w-3.5" />
                Conversations
            </p>
            <Button
                size="sm"
                onClick={onNewChat}
                className="h-7 gap-1.5 bg-sidebar px-2 text-xs text-white hover:bg-sidebar-primary"
            >
                <MessageSquarePlus className="h-3.5 w-3.5" />
                New
            </Button>
        </div>

        <div className="flex-1 space-y-1.5 overflow-y-auto p-2">
            {loading ? (
                [0, 1, 2].map((key) => (
                    <div key={key} className="animate-pulse rounded-lg border border-border p-2.5">
                        <div className="h-3 w-16 rounded bg-muted" />
                        <div className="mt-2 h-3.5 w-full rounded bg-muted" />
                    </div>
                ))
            ) : !sessions.length ? (
                <div className="px-3 py-8 text-center">
                    <MessageSquare className="mx-auto h-7 w-7 text-muted-foreground/50" />
                    <p className="mt-2 text-xs font-medium text-foreground">No conversations yet</p>
                    <p className="mt-1 text-[11px] leading-relaxed text-muted-foreground">
                        Ask a question and it will be saved here.
                    </p>
                </div>
            ) : (
                sessions.map((session) => {
                    const isActive = session.session_id === activeSessionId;
                    const domainMeta = getDomainMeta(session.domain);

                    return (
                        <button
                            key={session.id}
                            type="button"
                            onClick={() => onSelect(session.session_id)}
                            className={cn(
                                "w-full rounded-lg border p-2.5 text-left transition-colors",
                                isActive
                                    ? "border-sidebar-primary bg-sidebar/10"
                                    : "border-border bg-card hover:border-sidebar-primary/50 hover:bg-muted/50"
                            )}
                        >
                            <div className="flex items-center justify-between gap-2">
                                <span
                                    className={cn(
                                        "rounded-full px-1.5 py-0.5 text-[9px] font-semibold uppercase",
                                        domainMeta.tint
                                    )}
                                >
                                    {domainMeta.label}
                                </span>
                                <span className="flex shrink-0 items-center gap-1 text-[10px] text-muted-foreground">
                                    <Clock className="h-2.5 w-2.5" />
                                    {relativeDate(session.last_activity || session.created_date)}
                                </span>
                            </div>

                            <p className="mt-1.5 truncate text-xs font-medium text-foreground">
                                {session.title || "Untitled conversation"}
                            </p>
                            <p className="mt-0.5 text-[10px] text-muted-foreground">
                                {session.message_count || 0}{" "}
                                {session.message_count === 1 ? "message" : "messages"}
                            </p>
                        </button>
                    );
                })
            )}
        </div>
    </div>
);

export default CopilotSessionList;
